import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectDoctor, selectFunc } from 'redux/features/auth/authSlice';
import { gerarHorariosAgenda, inserirEncaixe } from 'services/agenda';
import { resolveUsuarioAgenda } from 'utils/agenda';
import { getApiErrorMessage } from 'utils/apiErrors';
import { formatDateISO } from 'utils/helpers';

function dataAgendaLocal(valor) {
  if (valor instanceof Date) return formatDateISO(valor);
  const texto = String(valor ?? '');
  if (/^\d{4}-\d{2}-\d{2}/.test(texto)) return texto.slice(0, 10);
  const data = new Date(texto);
  return Number.isNaN(data.getTime()) ? texto.slice(0, 10) : formatDateISO(data);
}

export function useAgendaActions({
  medicoSelecionado,
  dataSelecionada,
  agendaContext = null,
  onSuccess,
} = {}) {
  const func = useSelector(selectFunc);
  const doctor = useSelector(selectDoctor);
  const [showModalEncaixe, setShowModalEncaixe] = useState(false);
  const [horarioSugestaoEncaixe, setHorarioSugestaoEncaixe] = useState('');

  const getCodMedicoEfetivo = useCallback(() => {
    return medicoSelecionado?.codMed || medicoSelecionado?.CODMED || agendaContext?.CODMED || null;
  }, [medicoSelecionado, agendaContext]);

  const handleAbrirModalEncaixe = useCallback(() => {
    const codMedicoEfetivo = getCodMedicoEfetivo();

    if (!codMedicoEfetivo) {
      toast.warn('Selecione um médico antes de inserir o encaixe.');
      return;
    }

    const horarioBase = Number(agendaContext?.HORARIO) || 800;
    setHorarioSugestaoEncaixe(String(horarioBase + 1));
    setShowModalEncaixe(true);
  }, [agendaContext, getCodMedicoEfetivo]);

  const handleConfirmarEncaixe = useCallback(
    async (horarioDigitado) => {
      const codMedicoEfetivo = getCodMedicoEfetivo();

      const dataAgendaFormatada = dataAgendaLocal(dataSelecionada);
      const { codFunc } = resolveUsuarioAgenda({ func, doctor });

      try {
        const response = await inserirEncaixe({
          codMedico: Number(codMedicoEfetivo),
          dataAgenda: dataAgendaFormatada,
          horario: Number(horarioDigitado),
          codFunc,
        });

        toast.success(response?.mensagem || 'Encaixe registrado com sucesso.');
        if (onSuccess) await onSuccess();
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'Erro ao inserir encaixe.'));
      }
    },
    [dataSelecionada, doctor, func, getCodMedicoEfetivo, onSuccess]
  );

  const handleGerarHorarios = useCallback(async () => {
    const codMedicoEfetivo = getCodMedicoEfetivo();

    if (!codMedicoEfetivo) {
      toast.warn('Selecione um médico antes de gerar os horários.');
      return;
    }

    const { codFunc } = resolveUsuarioAgenda({ func, doctor });
    const dataAgendaFormatada = dataAgendaLocal(dataSelecionada);

    try {
      const response = await gerarHorariosAgenda({
        codMedico: Number(codMedicoEfetivo),
        dataAgenda: dataAgendaFormatada,
        codFunc,
      });

      const mensagem = response?.mensagem || 'Horários gerados com sucesso.';
      if (response?.horarios?.length > 0) {
        toast.success(mensagem);
      } else {
        toast.info(mensagem);
      }
      if (onSuccess) await onSuccess();
    } catch (error) {
      const mensagem = getApiErrorMessage(error, '');
      if (error.response?.status === 404) {
        toast.warning(mensagem || 'Nenhum horário configurado para este dia.');
      } else {
        toast.error(mensagem || 'Erro ao gerar horários.');
      }
    }
  }, [dataSelecionada, doctor, func, getCodMedicoEfetivo, onSuccess]);

  return {
    showModalEncaixe,
    setShowModalEncaixe,
    horarioSugestaoEncaixe,
    handleEncaixe: handleAbrirModalEncaixe,
    handleConfirmarEncaixe,
    handleGerarHorarios,
  };
}

export default useAgendaActions;
